<template>
  <DefineHeader v-slot="{ class: headerClass, classes }">
    <header :class="cn(headerClass, classes?.root)">
      <h1 :class="cn('text-2.5xl mb-3 font-semibold', classes?.title)">
        {{ tg("suggestions.title") }}
      </h1>

      <p :class="cn('font-light text-black/50', classes?.description)">
        {{ tg("suggestions.description") }}
      </p>
    </header>
  </DefineHeader>

  <main class="relative flex h-full justify-center md:flex-col">
    <div class="absolute inset-0 z-0 flex h-full md:flex-col" v-if="!isMobile">
      <div class="h-full w-full flex-1 bg-violet-200"></div>
      <div class="h-full w-full flex-1"></div>
    </div>

    <div class="min-md:container relative z-10">
      <PensionPortfolioNavbar
        :is-sticky="false"
        :classes="{ root: 'bg-transparent backdrop-blur-none', container: 'px-0' }"
        @exit="emits('close')"
        :backProps="{
        onClick: (e: MouseEvent) => {
          e.preventDefault()
          emits('close')
        },
      }"
      />

      <template v-if="isMobile">
        <ReuseHeader class="my-6 px-5" />

        <div class="mb-6 bg-violet-200 p-5 pb-3">
          <swiper-container class="flex flex-col gap-5" v-bind="swiperOptions">
            <swiper-slide
              v-for="(insight, index) in insights"
              :class="cn('slide-height', { 'marker:text-xxs list-inside list-disc': !isMobile })"
              :key="index"
            >
              <span class="text-sm font-light text-black/75 md:mb-5 md:block" v-html-format="insight" />
            </swiper-slide>
          </swiper-container>
        </div>
      </template>

      <div class="min-md:mt-10 flex w-full justify-between gap-20 md:flex-col md:px-5">
        <section v-if="!isMobile" aria-label="Benefits">
          <div class="min-md:max-w-large ms-auto">
            <ReuseHeader />
            <div class="flex size-full h-full flex-1 flex-col">
              <div class="min-md:mt-10">
                <ul class="flex flex-col gap-5">
                  <li
                    v-for="(insight, index) in insights"
                    :class="cn('slide-height marker:text-xxs list-inside list-disc', { '': !isMobile })"
                    :key="index"
                  >
                    <span class="text-sm font-light text-black/75 md:mb-5 md:block" v-html-format="insight" />
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section class="h-full overflow-y-auto" aria-label="Payment Form">
          <div class="min-md:max-w-large min-md:h-full flex flex-col">
            <!-- FORM -->

            <h2 class="mb-5 text-2xl font-semibold md:mb-4 md:text-base md:font-medium">
              {{ tg("title") }}
            </h2>

            <KycPensionsRecommendationCheckoutForm
              @success="emits('success', $event)"
              @close="emits('close')"
            >
              <template #footer="{ isSubmitLoading, isValid, hasPensionRecommendations, isError }">
                <footer class="relative flex flex-1 flex-col gap-10 pb-10">
                  <KycPensionsRecommendationCheckoutAgreement class="order-0" v-model="hasAcceptedTerms" />

                  <!-- Submit -->
                  <div class="order-1 flex w-full md:order-3">
                    <UiButton
                      class="h-10 w-full"
                      v-if="hasPensionRecommendations"
                      @click="emits('close')"
                      variant="outline"
                    >
                      {{ t("common.back") }}
                    </UiButton>
                    <UiButton
                      class="h-10 w-full"
                      v-else
                      :disabled="!isValid || !hasAcceptedTerms || isError"
                      :loading="isSubmitLoading"
                      type="submit"
                    >
                      {{ tg("suggestions.cta") }}
                    </UiButton>
                  </div>

                  <div
                    class="text-destructive order-3 mt-1 flex w-full items-center justify-center gap-1"
                    v-auto-animate
                    v-if="isError"
                  >
                    <Info class="size-4 rotate-180" />
                    <span>{{ tg("payme_error") }}</span>
                  </div>

                  <KycPensionsRecommendationCheckoutImportantToKnow class="order-2" />
                </footer>
              </template>
            </KycPensionsRecommendationCheckoutForm>
          </div>
        </section>
      </div>
    </div>
  </main>
</template>

<script setup lang="tsx">
import { Info } from "lucide-vue-next"
import { register } from "swiper/element/bundle"
import { type SwiperOptions } from "swiper/types"
import { useI18n } from "vue-i18n"

import { AppBreakpoints } from "@finq/app-base/lib/configs"

register()

const { t } = useI18n({ useScope: "local" })
const emits = defineEmits<{
  (e: "success", token: string): void
  (e: "close"): void
}>()

const { isMobile } = useDisplay()
const { tg } = useI18nUtils({ useScope: "local" })

const hasAcceptedTerms = ref(false)

const insights = computed(() => tg("insights"))

const [DefineHeader, ReuseHeader] = createReusableTemplate<{
  class?: ClassValue
  classes?: Partial<{
    root: ClassValue
    title: ClassValue
    description: ClassValue
  }>
}>()

const swiperOptions = computed((): SwiperOptions => {
  return {
    initialSlide: 0,
    slidesPerView: "auto",
    spaceBetween: 20,
    centeredSlides: true,
    keyboard: { enabled: true },
    pagination: { enabled: true, clickable: true },
    autoplay: {
      delay: 10000,
      disableOnInteraction: false,
    },

    breakpoints: {
      0: {
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 20,
      },
      [AppBreakpoints.md]: {
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
      },
    },
  }
})
</script>

<style lang="scss" scoped>
::part(bullet-active) {
  background: #222;
}

::part(bullet) {
  background: rgb(0, 0, 0, 0.5);
}

.slide-height {
  @screen md {
    height: 140px;
  }
}

:deep(strong) {
  font-weight: 500;
}
</style>

<i18n lang="json">
{
  "he": {
    "suggestions": {
      "title": "פירוט התובנות שלך מעבר לפינה",
      "description": "בהתבסס על המידע ששיתפת איתנו ועל נתוני המוצרים הפנסיוניים שלך שהתקבלו מהמסלקה הפנסיונית, נציג לך תובנות AI מקצועיות ומותאמות אישית ביחס לחיסכון הפנסיוני שלך. נדגיש, כי אין לראות בתובנות אלה ייעוץ או שיווק פנסיוני.",
      "cta": {
        "female": "המשיכי",
        "male": "המשך"
      }
    },
    "insights": {
      "male": [
        "לאחר עיון בתובנות, **תוכל לבחור אילו מבין התובנות תרצה ליישם**. אל דאגה, במסגרת הליך השיווק הפנסיוני המתמשך שנעניק לך, התובנות והבחירות שלך ייבדקו ויאושרו כהמלצות על ידי סוכן פנסיוני מוסמך מטעם FINQ, והכל בהתאם לצרכיך האישיים.",
        "לצורך קבלת שירות שיווק פנסיוני מתמשך ועל מנת שנוכל לממש עבורך את ההמלצות, **עליך למנות את FINQ לסוכנות הביטוח הפנסיונית שלך**.",
        "לאחר מכן אנו **נפעל ליישום התובנות** מול החברות המנהלות הרלוונטיות ונמשיך ללוות את המוצרים הפנסיוניים שלך גם בעתיד, למשך כל התקופה בה ייפוי הכוח המתמשך שתעניק לנו יעמוד בתוקפו, ו**הכל ללא עלות מצידך**.",
        "יחד עם זאת, אם לאחר שנציג לך את התובנות שהכנו עבורך, **תבחר שלא למנות את FINQ** לסוכנות הביטוח שלך ובכך להפסיק את ההליך, **FINQ תגבה ממך סכום חד פעמי בסך 400 ש״ח**  (כולל מע״מ) בעבור השירות שניתן לך עד כה."
      ],
      "female": [
        "לאחר עיון בתובנות, **תוכלי לבחור אילו מבין התובנות תרצי ליישם**. אל דאגה, במסגרת הליך השיווק הפנסיוני המתמשך שנעניק לך, התובנות והבחירות שלך ייבדקו ויאושרו כהמלצות על ידי סוכן פנסיוני מוסמך מטעם FINQ, והכל בהתאם לצרכיך האישיים.",
        "לצורך קבלת שירות שיווק פנסיוני מתמשך ועל מנת שנוכל לממש עבורך את ההמלצות, **עליך למנות את FINQ לסוכנות הביטוח הפנסיונית שלך**.",
        "לאחר מכן אנו **נפעל ליישום התובנות** מול החברות המנהלות הרלוונטיות ונמשיך ללוות את המוצרים הפנסיוניים שלך גם בעתיד, למשך כל התקופה בה ייפוי הכוח המתמשך שתעניקי לנו יעמוד בתוקפו, ו**הכל ללא עלות מצידך**.",
        "יחד עם זאת, אם לאחר שנציג לך את התובנות שהכנו עבורך, **תבחרי שלא למנות את FINQ** לסוכנות הביטוח שלך ובכך להפסיק את ההליך, **FINQ תגבה ממך סכום חד פעמי בסך 400 ש״ח**  (כולל מע״מ) בעבור השירות שניתן לך עד כה."
      ]
    },
    "title": {
      "male": "הכנס אמצעי תשלום",
      "female": "הכנסי אמצעי תשלום"
    },
    "payme_error": {
      "female": "משהו לא הסתדר. נסי שוב.",
      "male": "משהו לא הסתדר. נסה שוב."
    }
  }
}
</i18n>
